const preventDefault = (event) => {
  event.preventDefault();
  return false;
};

class CustomSlider extends HTMLElement {
  constructor() {
    super();

    this.currentPosition = 0;
    this.isDragging = false;
    this.startX = 0;
    this.startPosition = 0;

    this.handleDrag = this.handleDrag.bind(this);
    this.startDrag = this.startDrag.bind(this);
    this.endDrag = this.endDrag.bind(this);
    this.calculateBounds = this.calculateBounds.bind(this);
    this.debouncedBounds = debounce(this.calculateBounds, 100);
  }

  connectedCallback() {
    if (this.initialized) return;

    this.track =
      this.querySelector("[data-slider-track]") || this.firstElementChild;
    if (!this.track) return;

    this.slides = Array.from(this.track.querySelectorAll("[data-slider-item]"));
    if (this.slides.length < 2) return;

    this.clickThreshold = parseInt(
      this.getAttribute("click-threshold") || "5",
      10
    );
    this.dragThreshold = parseInt(
      this.getAttribute("drag-threshold") || "30",
      10
    );

    this.querySelectorAll("img").forEach((img) => {
      img.setAttribute("draggable", "false");
      img.addEventListener("dragstart", preventDefault, { passive: false });
    });

    this.calculateBounds();

    window.addEventListener("resize", this.debouncedBounds);
    window.addEventListener("load", this.calculateBounds);

    this.track.addEventListener("mousedown", this.startDrag);
    this.track.addEventListener("touchstart", this.startDrag, {
      passive: false,
    });

    this.initialized = true;
  }

  disconnectedCallback() {
    if (!this.initialized) return;

    window.removeEventListener("resize", this.debouncedBounds);
    window.removeEventListener("load", this.calculateBounds);

    this.track.removeEventListener("mousedown", this.startDrag);
    this.track.removeEventListener("touchstart", this.startDrag);

    document.removeEventListener("mousemove", this.handleDrag);
    document.removeEventListener("touchmove", this.handleDrag);
    document.removeEventListener("mouseup", this.endDrag);
    document.removeEventListener("touchend", this.endDrag);

    this.initialized = false;
  }

  startDrag(event) {
    if (event.type === "mousedown" && event.button !== 0) return;

    this.isDragging = false;
    this.startX =
      event.type === "touchstart" ? event.touches[0].clientX : event.clientX;
    this.startPosition = this.currentPosition;

    document.addEventListener("mousemove", this.handleDrag);
    document.addEventListener("touchmove", this.handleDrag, { passive: false });
    document.addEventListener("mouseup", this.endDrag);
    document.addEventListener("touchend", this.endDrag);

    event.preventDefault();
  }

  handleDrag(event) {
    const currentX =
      event.type === "touchmove" ? event.touches[0].clientX : event.clientX;
    const diff = this.startX - currentX;

    if (!this.isDragging && Math.abs(diff) < this.clickThreshold) return;

    if (!this.isDragging) {
      this.isDragging = true;
      this.track.classList.add("dragging");
      document.body.classList.add("slider-dragging");
      this.track.style.transition = "none";
    }

    const nextPosition = Math.max(
      Math.min(this.startPosition - diff, 0),
      -this.maxPosition
    );
    this.currentPosition = nextPosition;
    this.track.style.transform = `translateX(${nextPosition}px)`;

    event.preventDefault();
  }

  endDrag(event) {
    document.removeEventListener("mousemove", this.handleDrag);
    document.removeEventListener("touchmove", this.handleDrag);
    document.removeEventListener("mouseup", this.endDrag);
    document.removeEventListener("touchend", this.endDrag);

    document.body.classList.remove("slider-dragging");

    if (!this.isDragging) {
      const target = event.target.closest("a");
      if (target) target.click();
      return;
    }

    this.isDragging = false;
    this.track.classList.remove("dragging");

    const movement = this.currentPosition - this.startPosition;

    if (this.cardWidth > 0) {
      if (movement <= -this.dragThreshold) {
        this.currentPosition = Math.max(
          this.startPosition - this.cardWidth,
          -this.maxPosition
        );
      } else if (movement >= this.dragThreshold) {
        this.currentPosition = Math.min(this.startPosition + this.cardWidth, 0);
      } else {
        this.currentPosition = this.startPosition;
      }
    } else {
      this.currentPosition = Math.max(
        Math.min(this.currentPosition, 0),
        -this.maxPosition
      );
    }

    this.slideTo(this.currentPosition);
  }

  slideTo(position) {
    this.currentPosition = position;
    this.track.style.transition = "transform 0.3s ease";
    this.track.style.transform = `translateX(${position}px)`;
  }

  calculateBounds() {
    const containerWidth = this.getBoundingClientRect().width;
    const trackStyles = window.getComputedStyle(this.track);

    const gapValue = parseFloat(
      trackStyles.columnGap || trackStyles.gap || "0"
    );
    const paddingLeft = parseFloat(trackStyles.paddingLeft || "0");
    const paddingRight = parseFloat(trackStyles.paddingRight || "0");

    this.gap = isNaN(gapValue) ? 0 : gapValue;

    const firstSlideRect = this.slides[0].getBoundingClientRect();
    this.cardWidth = firstSlideRect.width + this.gap;

    const trackWidth =
      this.cardWidth * this.slides.length + paddingLeft + paddingRight;
    this.maxPosition = Math.max(trackWidth - containerWidth, 0);

    this.currentPosition = Math.max(
      Math.min(this.currentPosition, 0),
      -this.maxPosition
    );

    const previousTransition = this.track.style.transition;
    this.track.style.transition = "none";
    this.track.style.transform = `translateX(${this.currentPosition}px)`;
    requestAnimationFrame(() => {
      this.track.style.transition = previousTransition;
    });
  }
}

if (!customElements.get("custom-slider")) {
  customElements.define("custom-slider", CustomSlider);
}

/*
    <custom-slider
      style="--aspect-ratio: {{ section.settings.aspect_ratio }}"
      class="collections-slider"
      drag-threshold="30"
      click-threshold="5">
      {% assign responsive_widths = "320,480,640,768,960,1200" | split: "," %}
      <div class="collections-track page-width" data-slider-track>
        {% for collection in collections %}
          {% assign collection_title = collection.title | strip %}
          {% assign collection_title_words = collection_title | split: ' ' %}
          {% assign collection_title_word_count = collection_title_words | size %}
          <a
            href="{{ collection.url }}"
            class="collections-slide"
            data-slider-item
            data-index="{{ forloop.index0 }}">
            <div class="lazy-wrapper collections-image-wrapper">
              {% if collection.image %}
                {% assign image_alt = collection.image.alt | default: collection.title %}
                {% capture image_srcset %}
                  {% for width in responsive_widths %}
                    {% assign width_int = width | strip | plus: 0 %}
                    {{ collection.image | image_url: width: width_int }} {{ width_int }}w{% unless forloop.last %}, {% endunless %}
                  {% endfor %}
                {% endcapture %}
                <img
                  class="lazy-fade"
                  src="{{ collection.image | image_url: width: 720 }}"
                  srcset="{{ image_srcset | strip_newlines | strip }}"
                  sizes="(min-width: 1200px) calc(33vw - 2rem), (min-width: 750px) calc(50vw - 2rem), calc(100vw - 3rem)"
                  loading="lazy"
                  decoding="async"
                  width="{{ collection.image.width }}"
                  height="{{ collection.image.height }}"
                  alt="{{ image_alt | escape }}">
              {% else %}
                {% comment %} fallback some online image {% endcomment %}
                {% capture placeholder_srcset %}
                  {% for width in responsive_widths %}
                    {% assign width_int = width | strip | plus: 0 %}
                    {% assign height_int = width_int | times: 5 | divided_by: 4 %}
                    https://picsum.photos/seed/upsell-1/{{ width_int }}/{{ height_int }} {{ width_int }}w{% unless forloop.last %}, {% endunless %}
                  {% endfor %}
                {% endcapture %}
                <img
                  class="lazy-fade"
                  src="{{ sample_image }}"
                  srcset="{{ placeholder_srcset | strip_newlines | strip }}"
                  sizes="(min-width: 1200px) calc(33vw - 2rem), (min-width: 750px) calc(50vw - 2rem), calc(100vw - 3rem)"
                  loading="lazy"
                  decoding="async"
                  width="720"
                  height="900"
                  alt="{{ collection.title | escape }}">
              {% endif %}
            </div>

            <div class="collections-floating">
              <div class="collections-count">{{ collection.all_products.size }}</div>
              <div class="collections-name{% if collection_title_word_count > 1 %} collections-name--stacked{% endif %}">
                {% for word in collection_title_words %}
                  <span class="collections-name__part">{{ word }}</span>
                {% endfor %}
              </div>
            </div>

            <div class="collection-overlay">
              <div class="collections-overlay-name">{{ collection_title }}</div>
              <div class="collections-overlay-count">{{ collection.all_products.size }}</div>
            </div>
          </a>
        {% endfor %}
      </div>
    </custom-slider>
*/

// ------------------PARALLAX (Reusable)-------------------------
class ParallaxImagePair {
  constructor(options) {
    this.container = options.container;
    this.leftImage = options.leftImage;
    this.rightImage = options.rightImage;
    this.intensity =
      typeof options.intensity === "number" ? options.intensity : 0.5;
    this.rotatableImages = Array.isArray(options.rotateTargets)
      ? options.rotateTargets
      : [];
    this.swapImages = Array.isArray(options.swapTargets)
      ? options.swapTargets
      : this.rotatableImages;
    this.swapIntervalMs =
      typeof options.swapInterval === "number" ? options.swapInterval : 4000;
    this.swapDuration =
      typeof options.swapDuration === "number" ? options.swapDuration : 450;
    this.animationFrame = null;
    this.swapTimer = null;
    this.isSwapping = false;

    if (!this.container || !this.leftImage || !this.rightImage) return;
    if (window.innerWidth <= 999) return; // desktop only

    this.leftImage.style.willChange = "transform";
    this.rightImage.style.willChange = "transform";

    this.handleMouseMove = this.handleMouseMove.bind(this);
    this.handleMouseLeave = this.handleMouseLeave.bind(this);
    this.swapRandomImages = this.swapRandomImages.bind(this);
    this.handleVisibilityChange = this.handleVisibilityChange.bind(this);

    this.container.addEventListener("mousemove", this.handleMouseMove);
    this.container.addEventListener("mouseleave", this.handleMouseLeave);

    this.rotatableImages.forEach((img) => {
      img.style.setProperty("--flowers-rotate", "0deg");
      img.style.setProperty("--flowers-scale", "1");
      img.style.opacity = "1";
    });

    if (this.swapImages.length >= 2) {
      this.startSwapTimer();
      document.addEventListener(
        "visibilitychange",
        this.handleVisibilityChange
      );
    }
  }

  handleMouseMove(e) {
    if (this.animationFrame) {
      cancelAnimationFrame(this.animationFrame);
    }

    this.animationFrame = requestAnimationFrame(() => {
      const rect = this.container.getBoundingClientRect();
      const centerX = rect.left + rect.width / 2;
      const centerY = rect.top + rect.height / 2;

      const mouseX = (e.clientX - centerX) / (rect.width / 2);
      const mouseY = (e.clientY - centerY) / (rect.height / 2);

      const moveX = mouseX * this.intensity * 100;
      const moveY = mouseY * this.intensity * 100;

      this.leftImage.style.transform = `translate(${-moveX}px, ${-moveY}px)`;
      this.rightImage.style.transform = `translate(${moveX}px, ${moveY}px)`;

      if (this.rotatableImages.length) {
        this.updateImageRotations(e);
      }
    });
  }

  handleMouseLeave() {
    if (this.animationFrame) {
      cancelAnimationFrame(this.animationFrame);
      this.animationFrame = null;
    }

    this.leftImage.style.transition = "transform 0.6s ease-out";
    this.rightImage.style.transition = "transform 0.6s ease-out";

    this.leftImage.style.transform = "translate(0, 0)";
    this.rightImage.style.transform = "translate(0, 0)";

    setTimeout(() => {
      this.leftImage.style.transition = "";
      this.rightImage.style.transition = "";
    }, 600);

    this.rotatableImages.forEach((img) => {
      img.style.setProperty("--flowers-rotate", "0deg");
    });
  }

  updateImageRotations(event) {
    const maxAngle = 24;
    this.rotatableImages.forEach((img) => {
      const rect = img.getBoundingClientRect();
      const centerX = rect.left + rect.width / 2;
      const centerY = rect.top + rect.height / 2;
      const angleRad = Math.atan2(
        event.clientY - centerY,
        event.clientX - centerX
      );
      const angleDeg = (angleRad * 180) / Math.PI;
      const adjusted = Math.max(Math.min(angleDeg * 0.45, maxAngle), -maxAngle);
      img.style.setProperty("--flowers-rotate", `${adjusted}deg`);
    });
  }

  startSwapTimer() {
    if (this.swapTimer || this.swapImages.length < 2) return;
    this.swapTimer = window.setInterval(
      this.swapRandomImages,
      this.swapIntervalMs
    );
  }

  stopSwapTimer() {
    if (!this.swapTimer) return;
    window.clearInterval(this.swapTimer);
    this.swapTimer = null;
  }

  handleVisibilityChange() {
    if (document.visibilityState === "hidden") {
      this.stopSwapTimer();
    } else {
      this.startSwapTimer();
    }
  }

  animateImageState(img, scale, opacity) {
    return new Promise((resolve) => {
      window.requestAnimationFrame(() => {
        img.style.setProperty("--flowers-scale", String(scale));
        if (typeof opacity === "number") {
          img.style.opacity = String(opacity);
        }
      });
      window.setTimeout(resolve, this.swapDuration);
    });
  }

  swapImageSources(imgA, imgB) {
    const srcA = imgA.getAttribute("src");
    const srcsetA = imgA.getAttribute("srcset");
    const altA = imgA.getAttribute("alt");
    const indexA = imgA.getAttribute("data-index");

    const srcB = imgB.getAttribute("src");
    const srcsetB = imgB.getAttribute("srcset");
    const altB = imgB.getAttribute("alt");
    const indexB = imgB.getAttribute("data-index");

    if (srcA !== null) {
      imgB.setAttribute("src", srcA);
    } else {
      imgB.removeAttribute("src");
    }
    if (srcsetA) {
      imgB.setAttribute("srcset", srcsetA);
    } else {
      imgB.removeAttribute("srcset");
    }
    if (altA !== null) {
      imgB.setAttribute("alt", altA);
    }
    if (indexA !== null) {
      imgB.setAttribute("data-index", indexA);
    } else {
      imgB.removeAttribute("data-index");
    }

    if (srcB !== null) {
      imgA.setAttribute("src", srcB);
    } else {
      imgA.removeAttribute("src");
    }
    if (srcsetB) {
      imgA.setAttribute("srcset", srcsetB);
    } else {
      imgA.removeAttribute("srcset");
    }
    if (altB !== null) {
      imgA.setAttribute("alt", altB);
    }
    if (indexB !== null) {
      imgA.setAttribute("data-index", indexB);
    } else {
      imgA.removeAttribute("data-index");
    }
  }

  swapRandomImages() {
    if (this.isSwapping || this.swapImages.length < 2) return;
    const pool = this.swapImages.filter((img) => img.isConnected);
    if (pool.length < 2) return;

    const firstIndex = Math.floor(Math.random() * pool.length);
    let secondIndex = firstIndex;
    while (secondIndex === firstIndex) {
      secondIndex = Math.floor(Math.random() * pool.length);
    }

    const imgA = pool[firstIndex];
    const imgB = pool[secondIndex];

    this.isSwapping = true;

    Promise.all([
      this.animateImageState(imgA, 0.4, 0),
      this.animateImageState(imgB, 0.4, 0),
    ])
      .then(() => {
        this.swapImageSources(imgA, imgB);
        return Promise.all([
          this.animateImageState(imgA, 1, 1),
          this.animateImageState(imgB, 1, 1),
        ]);
      })
      .catch(() => {})
      .finally(() => {
        this.isSwapping = false;
      });
  }
}

const initParallaxPair = ({
  containerSelector,
  leftSelector,
  rightSelector,
  intensity,
  rotateSelector,
  swapSelector,
  swapInterval,
  swapDuration,
}) => {
  const container = document.querySelector(containerSelector);
  const leftImage = container ? container.querySelector(leftSelector) : null;
  const rightImage = container ? container.querySelector(rightSelector) : null;
  if (!container || !leftImage || !rightImage) return null;
  const rotateTargets =
    container && rotateSelector
      ? Array.from(container.querySelectorAll(rotateSelector))
      : [];
  const swapTargets =
    container && swapSelector
      ? Array.from(container.querySelectorAll(swapSelector))
      : rotateTargets;
  return new ParallaxImagePair({
    container,
    leftImage,
    rightImage,
    intensity,
    rotateTargets,
    swapTargets,
    swapInterval,
    swapDuration,
  });
};

// ------------------CUSTOM FAQ (Web Component)-------------------------
class CustomFaq extends HTMLElement {
  constructor() {
    super();

    this.TRANSITION_DURATION = 250;
    this.isAnimating = false;
    this.sectionId = this.getAttribute("data-section-id") || "";
    this.allowMultiple = this.getAttribute("data-allow-multiple") === "true";

    this.handleAccordion = this.handleAccordion.bind(this);
  }

  connectedCallback() {
    if (this.initialized) return;

    // Find all accordion items within this component
    this.items = Array.from(
      this.querySelectorAll(".accordion_item-basic, .faq-basic")
    );
    if (this.items.length === 0) return;

    // Find all buttons and collapses
    this.buttons = Array.from(this.querySelectorAll("[data-accordion-toggle]"));
    this.collapses = Array.from(this.querySelectorAll(".accordion-collapse"));

    if (this.buttons.length === 0 || this.collapses.length === 0) return;

    // Check for initial active index
    const initialActiveIndex = this.getAttribute("data-initial-active");
    let initialActiveButton = null;
    let initialActiveCollapse = null;

    if (initialActiveIndex !== null) {
      const index = parseInt(initialActiveIndex, 10);
      if (!isNaN(index) && index >= 0 && index < this.buttons.length) {
        initialActiveButton = this.buttons[index];
        const targetId = initialActiveButton.getAttribute("aria-controls");
        initialActiveCollapse = document.getElementById(targetId);
      }
    }

    // Initialize all accordions as closed
    this.collapses.forEach((collapse) => {
      collapse.style.height = "0";
    });

    // If initial active is specified, open it
    if (initialActiveButton && initialActiveCollapse) {
      initialActiveButton.setAttribute("aria-expanded", "true");
      initialActiveCollapse.classList.add("show");
      const item =
        initialActiveButton.closest(".accordion_item-basic") ||
        initialActiveButton.closest(".faq-basic");
      if (item) item.classList.add("active");

      // Set height to auto after a brief delay to allow for smooth initial render
      requestAnimationFrame(() => {
        const contentHeight = initialActiveCollapse.scrollHeight;
        initialActiveCollapse.style.height = `${contentHeight}px`;
        setTimeout(() => {
          initialActiveCollapse.style.height = "auto";
        }, this.TRANSITION_DURATION);
      });
    }

    // Add event listeners to all buttons
    this.buttons.forEach((button) => {
      button.addEventListener("click", this.handleAccordion);
    });

    this.initialized = true;
  }

  disconnectedCallback() {
    if (!this.initialized) return;

    // Remove event listeners
    this.buttons.forEach((button) => {
      button.removeEventListener("click", this.handleAccordion);
    });

    this.initialized = false;
  }

  handleAccordion(evt) {
    // Prevent rapid clicks during animation
    if (this.isAnimating) return;

    const button = evt.currentTarget;
    const isExpanded = button.getAttribute("aria-expanded") === "true";
    const targetId = button.getAttribute("aria-controls");
    const targetCollapse = document.getElementById(targetId);

    if (!targetCollapse || targetCollapse.classList.contains("collapsing"))
      return;

    // Set animation state
    this.isAnimating = true;

    // Close all other accordions if not allowing multiple
    if (!this.allowMultiple) {
      this.buttons.forEach((btn) => {
        if (btn !== button) {
          const otherTargetId = btn.getAttribute("aria-controls");
          const otherCollapse = document.getElementById(otherTargetId);

          if (otherCollapse && otherCollapse.classList.contains("show")) {
            btn.setAttribute("aria-expanded", "false");
            otherCollapse.classList.add("collapsing");
            otherCollapse.classList.remove("show");

            otherCollapse.style.height = `${otherCollapse.scrollHeight}px`;
            void otherCollapse.offsetHeight;
            otherCollapse.style.height = "0";

            setTimeout(() => {
              otherCollapse.classList.remove("collapsing");
              const item =
                btn.closest(".accordion_item-basic") ||
                btn.closest(".faq-basic");
              if (item) item.classList.remove("active");
            }, this.TRANSITION_DURATION);
          }
        }
      });
    }

    // Toggle current accordion
    if (!isExpanded) {
      button.setAttribute("aria-expanded", "true");
      targetCollapse.classList.add("collapsing", "show");
      const item =
        button.closest(".accordion_item-basic") || button.closest(".faq-basic");
      if (item) item.classList.add("active");

      targetCollapse.style.height = "0";
      const contentHeight = targetCollapse.scrollHeight;
      targetCollapse.style.height = `${contentHeight}px`;

      setTimeout(() => {
        targetCollapse.classList.remove("collapsing");
        targetCollapse.style.height = "auto";
        this.isAnimating = false;
      }, this.TRANSITION_DURATION);
    } else {
      button.setAttribute("aria-expanded", "false");
      targetCollapse.classList.add("collapsing");
      targetCollapse.classList.remove("show");

      targetCollapse.style.height = `${targetCollapse.scrollHeight}px`;
      void targetCollapse.offsetHeight;
      targetCollapse.style.height = "0";

      setTimeout(() => {
        targetCollapse.classList.remove("collapsing");
        const item =
          button.closest(".accordion_item-basic") ||
          button.closest(".faq-basic");
        if (item) item.classList.remove("active");
        this.isAnimating = false;
      }, this.TRANSITION_DURATION);
    }
  }
}

if (!customElements.get("custom-faq")) {
  customElements.define("custom-faq", CustomFaq);
}

/* 
  <custom-faq data-section-id="{{ section.id }}" data-allow-multiple="false">
    {% for faq in blocks %}
      {% render "faq-basic"
        , question: faq.settings.question
        , answer: faq.settings.answer
        , sectionId: section.id
        , index: forloop.index
      %}
    {% endfor %}
  </custom-faq>
*/
